import { useRef, useEffect, useState, useMemo } from 'react';
import { useInterval } from 'beautiful-react-hooks';

export const useIsMounted = () => {
    const isMountedRef = useRef(false);

    useEffect(() => {
        isMountedRef.current = true;
    }, []);

    return isMountedRef.current;
};

interface State {
    value?: any,
    prevPropValue?: any
}

export const useStateBasedOnProps = (propValue: any) => {
    const [, update] = useState<boolean>(false);

    const [state, setState] = useMemo(() => {
        const state: State = {};
        return [
            state,
            (value: any) => {
                if (value instanceof Function) {
                    state.value = value(state.value);
                } else {
                    state.value = value;
                }
                update((tick) => !tick);
            }
        ];
    }, [update]);

    if (state.prevPropValue !== propValue) {
        state.prevPropValue = propValue;
        state.value = propValue;
    }

    return [state.value, setState];
}

export const useWakeUp = (callback: () => void) => {
    const WAKE_TIMEOUT = 5000;
    const lastTime = useRef((new Date()).getTime());

    useInterval(() => {
        const currentTime = (new Date()).getTime();

        if (currentTime > (lastTime.current + WAKE_TIMEOUT + 2000)) {
            callback();
        }

        lastTime.current = currentTime;
    }, WAKE_TIMEOUT);

    return null;
}

export const useUpdatingMemo = <T>(fn: () => T, dependencies: any[], updateInterval?: number): T => {
    const [counter, setCounter] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCounter(prev => prev + 1);
        }, updateInterval ?? 60000);

        return () => {
            clearInterval(interval);
        }
    }, [updateInterval]);

    return useMemo(fn, [...dependencies, counter]);
}
